// FILE: src/components/feedback/FeedbackDetailPanel.js
//
// Right-hand detail drawer for a single feedback item. Shows the full record
// and exposes admin actions: change status, add an admin note, mark duplicate,
// mark released. All writes go through feedbackService.updateFeedback (RLS
// admin-gated). On success the parent list is patched via onUpdated.

import React, { useState, useEffect } from 'react';
import { updateFeedback, FEEDBACK_STATUSES } from '../../services/feedbackService';
import { styles, pill, STATUS_COLORS, SEVERITY_COLORS, formatDate, certLabel } from './feedbackStyles';

const drawer = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2100, display: 'flex', justifyContent: 'flex-end' },
  panel: { width: 'min(480px, 100%)', height: '100%', background: '#fff', overflowY: 'auto', boxShadow: '-4px 0 18px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' },
  head: { padding: '18px 22px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '12px' },
  close: { marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#888' },
  section: { padding: '18px 22px', borderBottom: '1px solid #f2f2f2' },
  meta: { display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: '0.82rem', color: '#555', marginTop: '10px' },
  metaItem: { display: 'flex', gap: '6px' },
  metaLabel: { color: '#9097a8', textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '0.68rem', alignSelf: 'center' },
  desc: { fontSize: '0.95rem', color: '#222', lineHeight: 1.55, whiteSpace: 'pre-wrap', margin: '4px 0 0' },
  sectionTitle: { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888', marginBottom: '8px', fontWeight: 700 },
  actionRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' },
  msg: (ok) => ({ marginTop: '10px', fontSize: '0.82rem', color: ok ? '#2e7d32' : '#c62828' }),
};

export default function FeedbackDetailPanel({ item, adminEmail, onUpdated, onClose }) {
  const [status, setStatus] = useState(item?.status || 'New');
  const [note, setNote]     = useState(item?.admin_notes || '');
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState(null);

  useEffect(() => {
    setStatus(item?.status || 'New');
    setNote(item?.admin_notes || '');
    setMsg(null);
  }, [item]);

  if (!item) return null;

  async function persist(changes, successText) {
    setBusy(true); setMsg(null);
    const { data, error } = await updateFeedback(item.id, { resolvedBy: adminEmail, ...changes });
    setBusy(false);
    if (error) { setMsg({ ok: false, text: error.message || 'Update failed' }); return; }
    setMsg({ ok: true, text: successText || 'Saved' });
    if (data) onUpdated(data);
  }

  const meta = item.metadata || {};

  return (
    <div style={drawer.overlay} onClick={onClose} data-testid="feedback-detail">
      <div style={drawer.panel} onClick={(e) => e.stopPropagation()}>
        <div style={drawer.head}>
          <span style={pill(STATUS_COLORS, item.status)}>{item.status}</span>
          <span style={pill(SEVERITY_COLORS, item.severity)}>{item.severity}</span>
          <button style={drawer.close} onClick={onClose} aria-label="Close detail">×</button>
        </div>

        <div style={drawer.section}>
          <div style={drawer.sectionTitle}>{item.category}</div>
          {item.title && <p style={{ ...drawer.desc, fontWeight: 700 }}>{item.title}</p>}
          <p style={drawer.desc}>{item.description}</p>
          <div style={drawer.meta}>
            <span style={drawer.metaItem}><span style={drawer.metaLabel}>Submitted</span> {formatDate(item.created_at)}</span>
            <span style={drawer.metaItem}><span style={drawer.metaLabel}>Source</span> {item.source}</span>
            <span style={drawer.metaItem}><span style={drawer.metaLabel}>Cert</span> {certLabel(item.certification)}</span>
            <span style={drawer.metaItem}><span style={drawer.metaLabel}>Page</span> {item.page_name || '—'}</span>
            <span style={drawer.metaItem}><span style={drawer.metaLabel}>Route</span> {item.route || '—'}</span>
            <span style={drawer.metaItem}><span style={drawer.metaLabel}>User</span> {item.user_id ? item.user_id.slice(0, 8) + '…' : 'anonymous'}</span>
            {item.discord_username && <span style={drawer.metaItem}><span style={drawer.metaLabel}>Discord</span> {item.discord_username}</span>}
          </div>
        </div>

        {(item.question_id || item.exam_domain) && (
          <div style={drawer.section}>
            <div style={drawer.sectionTitle}>Related question</div>
            <div style={drawer.meta}>
              {item.question_id && <span style={drawer.metaItem}><span style={drawer.metaLabel}>ID</span> {item.question_id}</span>}
              {item.exam_domain && <span style={drawer.metaItem}><span style={drawer.metaLabel}>Domain</span> {item.exam_domain}</span>}
              {item.session_id && <span style={drawer.metaItem}><span style={drawer.metaLabel}>Session</span> {String(item.session_id).slice(0, 8)}…</span>}
            </div>
            {meta.question_text && <p style={{ ...drawer.desc, fontSize: '0.86rem', color: '#555' }}>{meta.question_text}</p>}
          </div>
        )}

        {item.screenshot_url && (
          <div style={drawer.section}>
            <div style={drawer.sectionTitle}>Screenshot</div>
            <img src={item.screenshot_url} alt="Attached screenshot" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #eee' }} />
          </div>
        )}

        {/* ── Actions ─────────────────────────────────────────── */}
        <div style={drawer.section}>
          <div style={drawer.sectionTitle}>Status</div>
          <select
            style={{ ...styles.select, width: '100%' }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="feedback-status-select"
          >
            {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={drawer.actionRow}>
            <button style={styles.primaryBtn} disabled={busy} onClick={() => persist({ status, adminNotes: note }, 'Status updated')}>
              {busy ? 'Saving…' : 'Save status'}
            </button>
            <button style={styles.secondaryBtn} disabled={busy} onClick={() => { setStatus('Released'); persist({ status: 'Released', adminNotes: note }, 'Marked released'); }}>
              Mark released
            </button>
          </div>
        </div>

        <div style={drawer.section}>
          <div style={drawer.sectionTitle}>Admin notes</div>
          <textarea
            style={styles.textarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Triage notes, links, decisions…"
            data-testid="feedback-admin-note"
          />
          <div style={drawer.actionRow}>
            <button style={styles.primaryBtn} disabled={busy} onClick={() => persist({ adminNotes: note }, 'Note saved')}>
              Save note
            </button>
          </div>
          {item.resolved_at && (
            <p style={{ fontSize: '0.78rem', color: '#888', marginTop: '10px' }}>
              Resolved {formatDate(item.resolved_at)}{item.resolved_by ? ` by ${item.resolved_by}` : ''}
            </p>
          )}
          {msg && <p style={drawer.msg(msg.ok)}>{msg.text}</p>}
        </div>
      </div>
    </div>
  );
}
